import {
  action,
  makeAutoObservable,
  observable,
  runInAction,
  toJS,
} from "mobx";
import { Role } from "../api/Types";
import axios from "axios";
import { WithBooleanFlag } from "./core";

export type TSummaryEventData = {
  deadlineViolatedDefectCount: number;
  defectTotalCount: number;
  eliminatedDefectCount: number;
  notEliminatedDefectCount: number;
};

class DefectRegister {
  defectId = null;
  registryList = {};
  registryListHeatmap = {};
  isLoadingRegistryList = new WithBooleanFlag(false);
  initialPageNum = 1;
  initialPageSize = 0;
  eventLevelArr = ["Today"];
  infoPane = null;
  summaryEventData: TSummaryEventData = null;
  buildingArray = [];
  disconnectionsShutdown = true;
  disconnectionsWithout = true;
  eliminated = false;
  violated = true;
  notEliminated = true;
  notViolated = true;
  initialFilter = {
    isValid: true,
    isNotValid: false,
    Address: null,
    buildings: null,
    dateFrom: new Date(),
    dateTo: new Date(),
    deadlinesViolated: null,
    diameter: null,
    disconnections: null,
    eliminated: false,
    eventLevel: this.eventLevelArr.length
      ? this.eventLevelArr?.join(", ") //this.eventLevelArr?.toString()
      : null,
    gridTypeIds: [1, 2, 3, 4, 5, 6],
    districtIds: [
      1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19,
    ],
  };
  userProfile = null;
  collapsed = new WithBooleanFlag(false);
  siderCollapsed = false;
  autoRefreshInterval = 60000 * 3;

  isExpandedDefect = new WithBooleanFlag(false);
  spPanes = [
    {
      label: "Реестр дефектов",
      content: "Content of defect registry",
      key: "defectsTable",
      name: "defectsTable",
      closable: false,
    },
  ];

  searchMapCancelToken = null;

  constructor() {
    makeAutoObservable(
      this,
      {
        registryList: observable.deep,
        setRegistryList: action,
        getRegistry: action,
        registryListHeatmap: observable,
        setRegistryListHeatmap: action,
        getRegistryHeatmap: action,

        spPanes: observable,
        setSPPanes: action,
        setDefectId: action,

        infoPane: observable,
        setInfoPane: action,

        summaryEventData: observable,
        setEventSumary: action,
      },
      { deep: true },
    );
  }

  //actions
  getInfoPane() {
    axios
      .get("/sp/event/infopane", {
        params: this.parseFilter(this.initialFilter),
      })
      .then((res) => {
        this.setInfoPane(res);
      });
  }
  setInfoPane = (data) => {
    this.infoPane = data;
  };
  /**
   * Получение списка элементов для тепловой карты.
   */
  setRegistryListHeatmap = (data = {}) => {
    this.registryListHeatmap = toJS(data);
  };

  getRegistryHeatmap(dateFrom: Date, dateTo: Date, gridTypeIds: number[]) {
    axios
      .get(
        `/sp/event/searchMap?page=${this.initialPageNum}&count=${this.initialPageSize}`,
        {
          params: {
            ...this.parseFilter(this.initialFilter),
            dateFrom,
            dateTo,
            gridTypeIds,
            organizationIds: [Number(localStorage.getItem("userOrgId"))],
          },
        },
      )
      .then((res) => {
        this.setRegistryListHeatmap(res.data);
      });
  }

  /**
   * Получение списка элементов карты.
   */
  setRegistryList = (data = {}) => {
    runInAction(() => {
      this.registryList = toJS(data);
    });
  };

  async getRegistry() {
    this.isLoadingRegistryList.setTrue();
    try {
      this.setRegistryList();
      if (this.searchMapCancelToken) {
        this.searchMapCancelToken.abort();
      }
      this.searchMapCancelToken = new AbortController();
      const res = await axios.get(
        `/sp/event/searchMapOpt?page=${this.initialPageNum}&count=${this.initialPageSize}`,
        {
          signal: this.searchMapCancelToken.signal,
          params: {
            ...this.parseFilter(this.initialFilter),
            // Наложение этого фильтра может привести к тому, что диспетчер не увидит вновь созданный и
            // подтвержденный дефект на карте, если его организация не РСО.
            // Если организация текущего пользователя - РСО, то фильтр по ней автоматически накладывается в бэке.
            // organizationIds: [Number(localStorage.getItem("userOrgId"))],
          },
        },
      );
      if (res) {
        this.setRegistryList(res.data);
        this.getEventSummary();
        this.getInfoPane();
      }
    } catch (e) {
      console.log(e);
    } finally {
      this.isLoadingRegistryList.setFalse();
    }
  }

  getEventSummary() {
    axios
      .get(`/sp/event/summary`, {
        params: this.parseFilter(this.initialFilter),
      })
      .then((res) => {
        this.setEventSumary(res.data);
      });
  }
  setEventSumary = (data: TSummaryEventData) => {
    this.summaryEventData = data;
  };
  clearFilters() {
    this.violated = true;
    this.notViolated = true;
    this.eliminated = true;
    this.notEliminated = true;
    this.disconnectionsShutdown = true;
    this.disconnectionsWithout = true;
    this.eventLevelArr = [];
    const filter = {
      isValid: true,
      isNotValid: false,
      Address: null,
      buildings: null,
      dateFrom: new Date(),
      dateTo: new Date(),
      deadlinesViolated: null,
      diameter: null,
      disconnections: null,
      eliminated: null,
      eventLevel: null,
      gridTypeIds: [1, 2, 3, 4, 5, 6],
      districtIds: [
        1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19,
      ],
    };
    this.initialFilter = filter;
    this.getRegistry();
  }

  openDefect(defectId) {
    this.collapsed.setFalse();
    this.setDefectId(defectId);
  }

  setDefectId = (value = null) => {
    this.defectId = value;
  };

  setSPPanes = (panes) => {
    this.spPanes = panes;
  };

  hasToken() {
    return localStorage.getItem("token");
  }

  isUserInRole(roleId: Role) {
    return !!this.userProfile?.roles?.find((r) => r.id === roleId);
  }

  parseFilter(initialFilter) {
    const filter = { ...initialFilter };
    if (initialFilter.isValid && !initialFilter.isNotValid) {
      filter.isValid = true;
    }
    if (!initialFilter.isValid && initialFilter.isNotValid) {
      filter.isValid = false;
    }
    if (
      (initialFilter.isValid && initialFilter.isNotValid) ||
      (!initialFilter.isValid && !initialFilter.isNotValid)
    ) {
      delete filter.isValid;
    }
    delete filter.isNotValid;

    return filter;
  }
}

export default new DefectRegister();
